import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../../environments/environment';
import {
    ConditionalDimensionOperator,
    DataSetConditionalDimension,
    DataSetDimension,
    DataSetDimensionsGroup,
    DataSourceDimension,
    GeneralDimension
} from '../interfaces/dimensions';
import {Serializer, SerializerResponse, Serializers} from '../interfaces/serializer';
import {tap} from 'rxjs/operators';
import {SerializerUtils} from '../libraries/serializer-utils';
import {SelectOptions} from "../interfaces/form";
import {Site} from '../interfaces/site';

@Injectable({
  providedIn: 'root'
})
export class DimensionsService {

  public readonly operators: SelectOptions<ConditionalDimensionOperator, string> = [
    { key: 'equals', text: 'conditions.equals' },
    { key: 'not_equals', text: 'conditions.not_equals' },
    { key: 'contains', text: 'conditions.contains' },
    { key: 'not_contains', text: 'conditions.not_contains' },
    { key: 'begins_with', text: 'conditions.begins_with' },
    { key: 'ends_with', text: 'conditions.ends_with' }
  ];

  constructor(
    private readonly _http: HttpClient
  ) {}

  public getDataSourcesDimensions(dataset: Serializer<Site>): Observable<SerializerResponse<Serializers<DataSourceDimension>>> {
    return this._http.get<SerializerResponse<Serializers<DataSourceDimension>>>(`${environment.baseUrl}/api/${dataset.id}/data-source-dimensions`)
      .pipe(
        tap((response: SerializerResponse<Serializers<DataSourceDimension>>): void => {
          SerializerUtils.joinRelationships(response, ['data_source']);
        })
      );
  }

  public getGeneralDimensions(dataset: Serializer<Site>): Observable<SerializerResponse<Serializers<GeneralDimension>>> {
    return this._http.get(`${environment.baseUrl}/api/${dataset.id}/general-dimensions`)
      .pipe(
        tap((response: SerializerResponse<Serializers<GeneralDimension>>): void => {
          SerializerUtils.joinRelationships(response, ['data_set_dimensions_group']);
        })
      );
  }

  public getDataSetDimensions(dataset: Serializer<Site>): Observable<SerializerResponse<Serializers<DataSetDimension>>> {
    return this._http.get<SerializerResponse<Serializers<DataSetDimension>>>(`${environment.baseUrl}/api/${dataset.id}/data-set-dimensions`)
      .pipe(
        tap((response: SerializerResponse<Serializers<DataSetDimension>>) => {
         SerializerUtils.joinRelationships(response, ['data_set_dimensions_group', 'data_source']);
        })
      );
  }

  public updateDataSetDimension(dataset: Serializer<Site>, dimension: Serializer<DataSetDimension>, update: Partial<DataSetDimension>): Observable<SerializerResponse<Serializer<DataSetDimension>>> {
    return this._http.patch(`${environment.baseUrl}/api/${dataset.id}/data-set-dimensions/${dimension.id}`, update).pipe(
      tap((response: SerializerResponse<Serializer<DataSetDimension>>) => {
        SerializerUtils.joinRelationships(response, ['data_set_dimensions_group', 'data_source']);
      })
    );
  }

  public getDataSetConditionalDimensions(dataset: Serializer<Site>): Observable<SerializerResponse<Serializers<DataSetConditionalDimension>>> {
    return this._http.get<SerializerResponse<Serializers<DataSetConditionalDimension>>>(`${environment.baseUrl}/api/${dataset.id}/data-set-conditional-dimensions`).pipe(
      tap((response: SerializerResponse<Serializers<DataSetConditionalDimension>>) => {
        SerializerUtils.joinRelationships(response, ['data_set_dimensions_group']);
      })
    );
  }

  public createDataSetConditionalDimension(dataset: Serializer<Site>, dimension: Partial<DataSetConditionalDimension>): Observable<SerializerResponse<Serializer<DataSetConditionalDimension>>> {
    return this._http.post(`${environment.baseUrl}/api/${dataset.id}/data-set-conditional-dimensions`, dimension).pipe(
      tap((response: SerializerResponse<Serializer<DataSetConditionalDimension>>) => {
        SerializerUtils.joinRelationships(response, ['data_set_dimensions_group']);
      })
    );
  }

  public updateDataSetConditionalDimension(dataset: Serializer<Site>, dimension: Serializer<DataSetConditionalDimension>, update: Partial<DataSetConditionalDimension>): Observable<SerializerResponse<Serializer<DataSetConditionalDimension>>> {
    return this._http.patch(`${environment.baseUrl}/api/${dataset.id}/data-set-conditional-dimensions/${dimension.id}`, update)
      .pipe(
        tap((response: SerializerResponse<Serializer<DataSetConditionalDimension>>) => {
          SerializerUtils.joinRelationships(response, ['data_set_dimensions_group']);
        })
      );
  }

  public deleteDataSetConditionalDimension(dataset: Serializer<Site>, dimension: Serializer<DataSetConditionalDimension>): Observable<SerializerResponse<Serializer<DataSetConditionalDimension>>> {
    return this._http.delete(`${environment.baseUrl}/api/${dataset.id}/data-set-conditional-dimensions/${dimension.id}`);
  }

  public getDataSetDimensionsGroups(dataset: Serializer<Site>): Observable<SerializerResponse<Serializers<DataSetDimensionsGroup>>> {
    return this._http.get<SerializerResponse<Serializers<DataSetDimensionsGroup>>>(`${environment.baseUrl}/api/${dataset.id}/data-set-dimensions-groups`);
  }

  public createDataSetDimensionsGroup(dataset: Serializer<Site>, group: Partial<DataSetDimensionsGroup>): Observable<SerializerResponse<Serializer<DataSetDimensionsGroup>>> {
    return this._http.post(`${environment.baseUrl}/api/${dataset.id}/data-set-dimensions-groups`, group);
  }

  public updateDataSetDimensionsGroup(dataset: Serializer<Site>, group: Serializer<DataSetDimensionsGroup>, update: Partial<DataSetDimensionsGroup>): Observable<SerializerResponse<Serializer<DataSetDimensionsGroup>>> {
    return this._http.patch(`${environment.baseUrl}/api/${dataset.id}/data-set-dimensions-groups/${group.id}`, update);
  }

  public deleteDataSetDimensionsGroup(dataset: Serializer<Site>, group: Serializer<DataSetDimensionsGroup>): Observable<SerializerResponse<Serializer<DataSetDimensionsGroup>>> {
    return this._http.delete(`${environment.baseUrl}/api/${dataset.id}/data-set-dimensions-groups/${group.id}`);
  }

}
